import React, { useEffect, useState } from "react";
import { SectionWrapper } from "../components/SectionWrapper";
import Coupon, { RecapCouponState } from "../components/Coupon/Coupon";
import { IProfile } from "../IProfileData";
import { DEFAULT_TIMELOCK_STAKING_MIN_VALUE } from "@bundles/consts";
import FundsService from "@services/Funds.service";
import { connect } from "react-redux";
import { TReducers } from "@features/reducers";
import { formatNumberToUSD } from "@features/utils/functions";

type DiscountCodes = {
  20: {
    state: RecapCouponState;
    couponCode: string | null;
  };
  50: {
    state: RecapCouponState;
    couponCode: string | null;
  };
};

type PromotionsProps = {
  investorId?: number;
  profile?: IProfile;
  tier: string;
};

export const PromotionsComponent = ({
  investorId,
  profile,
  tier,
}: PromotionsProps) => {
  const [minValue, setMinValue] = useState(DEFAULT_TIMELOCK_STAKING_MIN_VALUE);
  const [discountCodes, setDiscountCodes] = useState<DiscountCodes>({
    20: {
      state: "disabled",
      couponCode: null,
    },
    50: {
      state: "disabled",
      couponCode: null,
    },
  });

  useEffect(() => {
    const couponType =
      profile?.recap_coupon_type === ""
        ? 20
        : (profile?.recap_coupon_type as any);
    const discountCode = profile?.recap_coupon_code;
    const timelockDiscountAvailable = profile?.timelock_bonus;

    const isNotRevealed =
      profile?.recap_coupon_type === "" && profile?.recap_coupon_code === "";

    const state = () => {
      if (["tier_0", "tier_0_kyc"].includes(tier)) {
        return "disabled_by_tier";
      }

      if (isNotRevealed && !timelockDiscountAvailable) {
        return "active";
      }

      if (discountCode) {
        return "revealed";
      }

      return "disabled";
    };

    const timelockStakingState = () => {
      if (["tier_0", "tier_0_kyc"].includes(tier)) {
        return "disabled_by_tier";
      }

      if (
        (isNotRevealed || (couponType === 20 && discountCode)) &&
        timelockDiscountAvailable
      ) {
        return "active";
      }

      if (!isNotRevealed) {
        return "revealed";
      }

      return "disabled";
    };

    setDiscountCodes((prev: any) => ({
      ...prev,
      [couponType]: {
        ...prev[couponType],
        couponCode: discountCode,
        state: state(),
      },
      50: {
        ...prev[50],
        couponCode: discountCode,
        state:
          timelockDiscountAvailable === true
            ? timelockStakingState()
            : "disabled",
      },
    }));
  }, [profile]);

  useEffect(() => {
    FundsService.getTimelockStakingSettings().then((resp) => {
      setMinValue((prev) => resp?.data?.timelock_tier_threshold_amount ?? prev);
    });
  }, []);

  return (
    <SectionWrapper title="Recap coupons">
      <p>
        As a BF Tier 1 verified investor you can qualify to get 20% off your
        first years Recap subscription. If you stake a minimum of{" "}
        {formatNumberToUSD(minValue)} with a one year lock in with us as a thank
        you we’ll give you 50% off your first year. Simply copy the below coupon
        code when upgrading your Recap account.
      </p>

      <div className="flex flex-col gap-8 mt-[50px]">
        {!profile?.timelock_bonus && (
          <Coupon
            couponCode={discountCodes[20].couponCode}
            discountAmount="20"
            state={discountCodes[20].state}
            setDiscountCodes={setDiscountCodes}
            investorId={investorId}
          />
        )}
        <Coupon
          investorId={investorId}
          couponCode={discountCodes[50].couponCode}
          discountAmount="50"
          state={discountCodes[50].state}
          setDiscountCodes={setDiscountCodes}
          timelockStakeMinValue={minValue}
        />
      </div>
    </SectionWrapper>
  );
};

const mapStateToProps = (store: TReducers) => ({
  tier: store.userInfo.tier,
});

export const Promotions = connect(mapStateToProps)(PromotionsComponent);
